// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ActionableNotification,
  Button,
  DataTableSkeleton,
  InlineNotification,
  Modal,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  ToastNotification,
} from '@carbon/react';
import { TrashCan } from '@carbon/icons-react';
import { accountTypeLabel, useUsers, useDeleteUser, deriveEffectiveRole } from '../../shared/users';
import type { InviteUserResult, UserWithOrg } from '../../shared/users';
import { useSharedSession } from '../../shared/auth/useSharedSession';
import { roleLabel, roleTagType } from './orgRole';
import type { TagType } from './orgRole';
import InviteUserModal from './InviteUserModal';
import styles from './UsersPage.module.css';

type Status = 'Active' | 'Awaiting first sign-in' | 'Inactive';

const STATUS_DOT_CLASS: Record<Status, string> = {
  Active: styles.statusDotActive,
  'Awaiting first sign-in': styles.statusDotInvited,
  Inactive: styles.statusDotInactive,
};

// Same toast pattern and duration as BulkImportPage's run-status toasts —
// one Admin Center convention for "action succeeded" feedback.
const TOAST_TIMEOUT_MS = 3000;

interface UserRow {
  id: string;
  name: string;
  initials: string;
  email: string;
  accountType: string;
  role: string | null;
  siteAccess: string;
  status: Status;
}

const FILTERS = ['All', 'Owners', 'Admins', 'Members', 'Viewers'] as const;
type Filter = (typeof FILTERS)[number];

// Each tab filters to the canonical role behind its Owner/Admin/Member/Viewer
// label (see `shared/roles/roleLabels.ts`) — not to IAM's own role name.
const FILTER_TO_ROLE: Record<Filter, string | null> = {
  All: null,
  Owners: 'operations_manager',
  Admins: 'admin',
  Members: 'operator',
  Viewers: 'executive',
};

// `UserWithOrg.roles` is the raw IAM role-name list (e.g. ["OrgAdmin"]) — a
// user can hold several, at different scopes (`role_assignments`). This
// screen shows the single rank-based role `deriveEffectiveRole` already
// derives for session/permission purposes (`helios/services/authService.ts`),
// so Admin Center and the rest of the app agree on "what role is this user"
// instead of inventing a second mapping.
//
// The Org Role column shows Admin Center's four customer-facing labels
// (Owner/Admin/Member/Viewer — the same four the filter tabs above already
// use) rather than IAM's own role names, per the #31297 taxonomy thread:
// Filippo confirmed Admin Center keeps these four labels, matched to the
// real IAM-backed role by rank/color rather than by name. `roleLabel`/
// `roleTagType`/`ROLE_TAG_TYPE` live in `./orgRole` so this list and the user
// detail page (#22697) share one definition, and the Home dashboard's role
// breakdown reads the same `shared/roles/roleLabels.ts` map.

// `access_resolved: false` means the BFF couldn't look up this user's IAM
// assignments (access unknown) — distinct from a resolved "no sites".
function siteAccessLabel(user: UserWithOrg): string {
  if (!user.access_resolved) return '—';
  if (user.all_sites) return 'All sites';
  if (user.sites.length === 0) return 'No sites';
  if (user.sites.length === 1) {
    const site = user.sites[0];
    return site.site_key ?? site.label ?? site.site_id;
  }
  return `${user.sites.length} sites`;
}

// Keyed by color (the class names happen to be named after the Owner/Admin/
// Member/Viewer labels above, since that's the taxonomy they were built for)
// rather than by the canonical role name, so `roleTagType` stays the single
// source for "which of the four colors does this role get".
const AVATAR_CLASS_BY_TAG_TYPE: Record<TagType, string> = {
  purple: styles.roleOwner,
  green: styles.roleAdmin,
  blue: styles.roleMember,
  gray: styles.roleViewer,
};

function initialsOf(firstName: string, lastName: string): string {
  const first = firstName.trim().charAt(0);
  const last = lastName.trim().charAt(0);
  return (first + last).toUpperCase() || '?';
}

// "pending" (IAM's AWAITING_FIRST_SIGN_IN) is a pre-provisioned employee who
// hasn't signed in yet, not a disabled account — its own state rather than
// lumped in with "Inactive", worded the same on the user detail page
// (AB#35253).
function statusOf(user: UserWithOrg): Status {
  if (user.status === 'active') return 'Active';
  if (user.status === 'pending') return 'Awaiting first sign-in';
  return 'Inactive';
}

/** Toast copy for a completed add (AB#35252): what the admin does next. */
function addedToast(result: InviteUserResult, name: string): { title: string; subtitle: string } {
  return {
    title:
      result.outcome === 'existing_federated'
        ? `${name} was already in your organization — role granted`
        : `${name} added`,
    subtitle:
      result.mode === 'federated'
        ? 'This user signs in with their company account and has no password.'
        : "This user can't sign in until you generate a temporary password.",
  };
}

function toRow(user: UserWithOrg): UserRow {
  const name = `${user.first_name} ${user.last_name}`.trim() || user.email;
  return {
    id: user.id,
    name,
    initials: initialsOf(user.first_name, user.last_name),
    email: user.email,
    accountType: accountTypeLabel(user),
    role: deriveEffectiveRole(user),
    siteAccess: siteAccessLabel(user),
    status: statusOf(user),
  };
}

function matchesSearch(user: UserRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
}

/**
 * Access > Users: the real org member list — `GET /api/v1/users` via
 * `useUsers()`, replacing the static mock (ADO #22692). Search and the
 * filter tabs are client-side over the already-fetched page (ADO #22694).
 * "Add user" opens `InviteUserModal` (ADO #22696, AB#35252), which creates the
 * IAM user and grants the chosen role as its own role-assignment calls
 * (ADR-0026) — a partial failure there (user created, grant incomplete)
 * surfaces as the warning banner above rather than a silent success (ADO
 * #31303). Role and site access are both resolved from real IAM role
 * assignments (ADO #31307 and #31302 resolved upstream by another PR,
 * consumed here via `deriveEffectiveRole` and `all_sites`/`sites`). Deleting
 * a user (ADO #22698) hits the real `DELETE /api/v1/users/{id}`; `useDeleteUser`
 * invalidates the same query `useUsers` reads, so the row drops out once the
 * refetch lands rather than this screen splicing its own copy of the list.
 */
export default function UsersPage() {
  const navigate = useNavigate();
  const { users: rawUsers, isLoading, error } = useUsers();
  const { permissions } = useSharedSession();
  const canAddUser = permissions.includes('iam.user.create');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<Filter>('All');
  const [isInviteOpen, setInviteOpen] = useState(false);
  const [inviteToast, setInviteToast] = useState<{ title: string; subtitle: string } | null>(null);
  // Set when the invite created the user but couldn't finish granting their
  // role/site access (ADO #31303's partial-failure requirement) — the modal
  // itself already closed, since the user does exist; this banner is the
  // admin's way back in to finish or retry it.
  const [inviteWarning, setInviteWarning] = useState<{ userId: string; message: string } | null>(
    null
  );
  const [userToDelete, setUserToDelete] = useState<UserRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { deleteUser, isDeleting } = useDeleteUser();

  const users = useMemo(() => rawUsers.map(toRow), [rawUsers]);

  const filteredUsers = useMemo(() => {
    const wantedRole = FILTER_TO_ROLE[activeFilter];
    return users.filter(
      user => matchesSearch(user, searchQuery) && (wantedRole === null || user.role === wantedRole)
    );
  }, [users, searchQuery, activeFilter]);

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setDeleteError(null);
    try {
      await deleteUser(userToDelete.id);
      setUserToDelete(null);
    } catch {
      setDeleteError(`Couldn't delete ${userToDelete.name}. Try again.`);
      setUserToDelete(null);
    }
  };

  return (
    <div>
      <div className={styles.title}>
        Users <span className={styles.userCount}>({users.length})</span>
      </div>

      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title={error}
          hideCloseButton
          className={styles.notification}
        />
      )}

      {deleteError && (
        <InlineNotification
          kind="error"
          lowContrast
          title={deleteError}
          onCloseButtonClick={() => setDeleteError(null)}
          className={styles.notification}
        />
      )}

      {inviteWarning && (
        <ActionableNotification
          kind="warning"
          lowContrast
          title="User created, but setup isn't finished"
          subtitle={inviteWarning.message}
          className={styles.notification}
          onCloseButtonClick={() => setInviteWarning(null)}
          actionButtonLabel="Open profile"
          onActionButtonClick={() => {
            const userId = inviteWarning.userId;
            setInviteWarning(null);
            navigate(`/admin-center/users/${userId}`);
          }}
        />
      )}

      <div className={styles.toolbar}>
        <Search
          size="lg"
          labelText="Search name or email"
          placeholder="Search name or email"
          className={styles.searchField}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        />
        <div className={styles.filters}>
          {FILTERS.map(filter => (
            <Button
              key={filter}
              kind={filter === activeFilter ? 'secondary' : 'ghost'}
              size="sm"
              className={filter === activeFilter ? styles.filterActive : styles.filter}
              aria-pressed={filter === activeFilter}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </Button>
          ))}
        </div>
        {/* Gated on the caller's permission, never their role (AB#35252). */}
        {canAddUser && (
          <Button
            kind="tertiary"
            size="sm"
            className={styles.inviteButton}
            onClick={() => setInviteOpen(true)}
          >
            Add user
          </Button>
        )}
      </div>

      {canAddUser && (
        <InviteUserModal
          open={isInviteOpen}
          onClose={() => setInviteOpen(false)}
          onSuccess={(result, name) => setInviteToast(addedToast(result, name))}
          onPartialFailure={(userId, message) => setInviteWarning({ userId, message })}
          onOpenUser={userId => navigate(`/admin-center/users/${userId}`)}
        />
      )}

      {inviteToast && (
        <ToastNotification
          className={styles.toast}
          kind="success"
          title={inviteToast.title}
          subtitle={inviteToast.subtitle}
          timeout={TOAST_TIMEOUT_MS}
          onClose={() => setInviteToast(null)}
          lowContrast
        />
      )}

      {isLoading ? (
        <DataTableSkeleton
          headers={[
            { key: 'name', header: 'Name' },
            { key: 'email', header: 'Email' },
            { key: 'signIn', header: 'Sign-in' },
            { key: 'role', header: 'Org role' },
            { key: 'site', header: 'Site access' },
            { key: 'status', header: 'Status' },
            { key: 'actions', header: 'Actions' },
          ]}
          rowCount={5}
          showHeader={false}
          showToolbar={false}
          size="md"
        />
      ) : !error && users.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>No users yet</div>
        </div>
      ) : !error && filteredUsers.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>No users match your filters</div>
        </div>
      ) : (
        <Table className={styles.table}>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Sign-in</TableHeader>
              <TableHeader>Org role</TableHeader>
              <TableHeader>Site access</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map(user => (
              <TableRow
                key={user.id}
                className={`${styles.rowClickable} ${user.status === 'Inactive' ? styles.rowInactive : ''}`}
                onClick={() => navigate(`/admin-center/users/${user.id}`)}
              >
                <TableCell className={styles.nameCell}>
                  {/* A focusable Link, not just the row's onClick, so keyboard
                      and screen-reader users can reach the detail page too —
                      the row click stays as a pointer-only convenience. */}
                  <Link
                    to={`/admin-center/users/${user.id}`}
                    className={styles.nameCellInner}
                    onClick={e => e.stopPropagation()}
                  >
                    <span
                      className={`${styles.avatar} ${AVATAR_CLASS_BY_TAG_TYPE[roleTagType(user.role)]}`}
                    >
                      {user.initials}
                    </span>
                    {user.name}
                  </Link>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.accountType}</TableCell>
                <TableCell>
                  <Tag type={roleTagType(user.role)} size="sm">
                    {roleLabel(user.role)}
                  </Tag>
                </TableCell>
                <TableCell>{user.siteAccess}</TableCell>
                <TableCell>
                  <span className={styles.statusCell}>
                    <span className={STATUS_DOT_CLASS[user.status]} />
                    {user.status}
                  </span>
                </TableCell>
                <TableCell>
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    iconDescription={`Delete ${user.name}`}
                    tooltipAlignment="end"
                    renderIcon={TrashCan}
                    onClick={e => {
                      e.stopPropagation();
                      setUserToDelete(user);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Modal
        open={userToDelete !== null}
        danger
        size="sm"
        modalHeading="Delete user"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={isDeleting}
        onRequestSubmit={handleConfirmDelete}
        onRequestClose={handleCancelDelete}
        onSecondarySubmit={handleCancelDelete}
      >
        {userToDelete && (
          <p>
            Remove <strong>{userToDelete.name}</strong> ({userToDelete.email}) from this
            organization? This can&apos;t be undone.
          </p>
        )}
      </Modal>
    </div>
  );
}
